import { Component, OnInit, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ForgotCredentialsResult } from '../../models/api/core/auth/forgot-credentials-result';
import { ForgotUsername } from '../../models/api/core/auth/forgot-username';
import { MessageBus } from '../../services/message/message-bus.service';
import { BaseComponent } from '../base/base.component';
import { ButtonComponent } from '../core/buttons/button.component';
import { InputComponent } from '../core/controls/input.component';
import { RecaptchaComponent } from '../core/controls/recaptcha.component';
import { ApiErrorLabelComponent } from '../core/labels/api-error-label.component';
import { ActionRowComponent } from '../core/panels/action-row.component';
import { LinkLabelComponent } from '../core/labels/link-label.component';
import { RequiredFieldsNoteComponent } from '../core/labels/required-fields-note.component';

@Component({
  selector: 'app-forgot-username',
  imports: [
    ButtonComponent,
    RouterModule,
    ActionRowComponent,
    InputComponent,
    ReactiveFormsModule,
    RecaptchaComponent,
    ApiErrorLabelComponent,
    LinkLabelComponent,
    RequiredFieldsNoteComponent,
  ],
  template: `
    @if (!emailSent) {
      <form
        class="tw-px-4 tw-pt-4 tw-pb-2 "
        [formGroup]="fg"
        (ngSubmit)="sendEmail()"
      >
        @if (error) {
          <div class="tw-flex tw-justify-center tw-pt-4 tw-pb-2">
            <app-api-error-label [error]="error" />
          </div>
        }
        <h2 class="tw-text-base tw-font-bold tw-py-4 tw-break-words">
          Forgot your username?
        </h2>
        <div class="tw-break-words">
          Please provide the email address associated with your account:
        </div>

        <app-required-fields-note />
        <div class="tw-pt-8 tw-pb-2">
          <app-input
            [control]="fg.controls.email"
            label="Email"
            [large]="true"
          />
        </div>

        @if (recaptchaSiteKey) {
          <div class="tw-pt-4 tw-pb-8 sm:tw-px-2">
            <app-recaptcha
              [control]="fg.controls.recaptcha"
              [siteKey]="recaptchaSiteKey"
            />
          </div>
        }
        @if (recaptchaLoadError) {
          <div class="tw-flex tw-justify-center tw-pt-4 tw-pb-2">
            <app-api-error-label
              error="Unable to load security verification. Please refresh or contact technical support."
            />
          </div>
        }
        <div class="tw-pr-0 tw-pb-10">
          <app-action-row>
            <app-button
              [disabled]="!fg.valid || recaptchaLoadError"
              type="submit"
              right2
              label="Send Email"
              [loading]="loading"
            />
            <app-button
              routerLink="/login"
              right3
              emphasis="Low"
              label="Cancel"
            />
          </app-action-row>
        </div>
      </form>
    } @else {
      <div class="tw-px-4 tw-pt-4 tw-pb-2 ">
        <div class="tw-font-bold tw-py-4">Email sent</div>
        <p>
          If a username is found, it will be sent to you by email. Please check
          your Inbox, Junk, and Spam folders for an email prior to contacting
          technical support.
        </p>
        <p class="tw-pb-8 tw-pt-4">
          For additional help, please contact
          <app-link-label (click)="messagBus.publish('show-technical-support')"
            >technical support</app-link-label
          >.
        </p>
      </div>
      <div class=" tw-pr-7 tw-pb-10">
        <app-action-row>
          <app-button routerLink="/login" right3 label="Back to Login" />
        </app-action-row>
      </div>
    }
  `,
  styles: ``,
})
export class ForgotUsernameComponent extends BaseComponent implements OnInit {
  protected messagBus = inject(MessageBus);

  emailSent = false;
  emailValidation = '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$';
  email: ForgotUsername;
  results: ForgotCredentialsResult;
  status: string;
  error: any;
  loading = false;
  recaptchaSiteKey: string | null = null;
  recaptchaLoadError = false;

  fg = new FormGroup({
    email: new FormControl<string>(null, [
      Validators.required,
      Validators.pattern(this.emailValidation),
    ]),
    recaptcha: new FormControl<string>(null, Validators.required),
  });

  async ngOnInit() {
    try {
      const config = await this.api.auth.getRecaptchaSiteKey();
      this.recaptchaSiteKey = config?.siteKey ?? null;
    } catch {
      this.recaptchaLoadError = true;
    }
  }

  async sendEmail() {
    if (!this.fg.valid) {
      this.fg.markAllAsTouched();
      return;
    }

    this.email = {
      email: this.fg.value.email,
      recaptcha: this.fg.value.recaptcha,
    };

    this.status = null;
    this.error = null;
    this.loading = true;
    try {
      await this.api.auth.getXsrf();
      this.results = await this.api.auth.forgotUsername(this.email);
      if (this.results === 'Success') {
        this.emailSent = true;
      } else {
        this.emailSent = false;
        this.error =
          'The system could not validate your information.  Make sure your email address is correct and try again.';
      }
    } catch (e) {
      this.emailSent = false;
      this.status = 'Failed';
      this.error = e;
    } finally {
      this.loading = false;
    }
  }
}
